import {
  ApiClient,
  type ApiClientOptions,
  type CursorPagedResponse,
} from '@/infra/api-client'
import type {
  BillingProfileResource,
  UpdateBillingProfileRequest,
} from './resources/billing-profile'
import type {
  BillingMethodResource,
  CreateBillingMethodRequest,
  SetupIntentResource,
  SetupStripePaymentMethodRequest,
} from './resources/billing-method'
import type { BillingOperationResponse } from './resources/billing-task-result'
import type {
  AddUserCouponRequest,
  UserCouponResource,
  CouponResource,
} from './resources/coupon'
import type {
  ListTransactionsRequest,
  TransactionResource,
} from './resources/transaction'
import { toURLSearchParams } from '@/utils/url-util'

/**
 * Billing APIs.
 */
export class BillingApi {
  readonly profiles
  readonly userCoupons
  readonly coupons
  readonly transactions

  constructor(options: ApiClientOptions) {
    this.profiles = new BillingProfileApi(options)
    this.userCoupons = new UserCouponApi(options)
    this.coupons = new CouponApi(options)
    this.transactions = new TransactionApi(options)
  }
}

export class BillingProfileApi extends ApiClient {
  getMemberBillingProfile(
    workspaceId: string,
    userId: string,
  ): Promise<BillingProfileResource> {
    return this._get(`v3/workspaces/${workspaceId}/billing-profiles/${userId}`)
  }

  getTeamBillingProfile(teamId: string): Promise<BillingProfileResource> {
    return this._get(`v3/teams/${teamId}/billing-profile`)
  }

  updateMemberBillingProfile(
    workspaceId: string,
    userId: string,
    data: UpdateBillingProfileRequest,
  ): Promise<BillingProfileResource> {
    return this._patch(
      `v3/workspaces/${workspaceId}/billing-profiles/${userId}`,
      data,
    )
  }

  updateTeamBillingProfile(
    teamId: string,
    data: UpdateBillingProfileRequest,
  ): Promise<BillingProfileResource> {
    return this._patch(`v3/teams/${teamId}/billing-profile`, data)
  }

  addBillingMethod(
    data: CreateBillingMethodRequest,
  ): Promise<BillingMethodResource> {
    return this._post('v3/billing-methods', data)
  }

  removeBillingMethod(
    billingMethodId: string,
  ): Promise<BillingOperationResponse> {
    return this._delete(`v3/billing-methods/${billingMethodId}`)
  }

  setupStripePaymentMethodIntent(
    data: SetupStripePaymentMethodRequest,
  ): Promise<SetupIntentResource> {
    return this._post(`v3/stripe-payment-methods/setup-intent`, data)
  }

  completeSetupStripePaymentMethodIntent(
    setupIntentId: string,
  ): Promise<BillingMethodResource> {
    return this._post(`v3/stripe-payment-methods/setup-intent/complete`, {
      setup_intent_id: setupIntentId,
    })
  }
}

export class UserCouponApi extends ApiClient {
  getCouponsForUser(
    workspaceId: string,
    userPublicId?: string,
    signal?: AbortSignal,
  ): Promise<UserCouponResource[]> {
    return this._get(
      `v3/workspaces/${workspaceId}/users/${userPublicId}/coupons`,
      undefined,
      { signal },
    )
  }

  addCouponToUser(
    code: string,
    idParams: AddUserCouponRequest,
  ): Promise<UserCouponResource> {
    return this._post(
      `v3/workspaces/${idParams.workspace_id}/users/${idParams.user_public_id}/coupons/${code}`,
      undefined,
    )
  }
}

export class CouponApi extends ApiClient {
  getCoupon(id: string, signal?: AbortSignal): Promise<CouponResource> {
    return this._get(`v3/coupons/${id}`, undefined, { signal })
  }
}

export class TransactionApi extends ApiClient {
  async listTransactions(
    request: ListTransactionsRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<TransactionResource>> {
    return await this._get(
      `v3/transactions`,
      toURLSearchParams({ ...request }),
      { signal },
    )
  }
}
